import ContactElement from "../components/elements/ContactElement.vue"
import RecordElement from "../components/elements/RecordElement.vue"
import SocialElement from "../components/elements/SocialElement.vue"
import TagElement from "../components/elements/TagElement.vue"
import TimeElement from "../components/elements/TimeElement.vue"
import UserElement from "../components/elements/UserElement.vue"
import LookupField from "./computed/LookupField.vue"
import RecordField from "./computed/RecordField.vue"
import { registerJunctionElements } from "./junction/registry"
import ReferenceField from "./reference/ReferenceField.vue"
import ReferenceInput from "./reference/ReferenceInput.vue"
import { registerReferenceEntries } from "./reference/registry"
import { registerFieldComponents } from "./registry"
import BooleanField from "./value/BooleanField.vue"
import BooleanInput from "./value/BooleanInput.vue"
import CurrencyField from "./value/CurrencyField.vue"
import CurrencyInput from "./value/CurrencyInput.vue"
import NumberInput from "./value/NumberInput.vue"
import ObjectInput from "./value/ObjectInput.vue"
import SegmentedSelect from "./value/SegmentedSelect.vue"
import SelectValueField from "./value/SelectValueField.vue"
import StatusSelect from "./value/StatusSelect.vue"
import StatusSelectField from "./value/StatusSelectField.vue"
import StringInput from "./value/StringInput.vue"
import TimestampInput from "./value/TimestampInput.vue"
import ValueField from "./value/ValueField.vue"
import ValueSelect from "./value/ValueSelect.vue"
import VisibilityField from "./value/VisibilityField.vue"

/**
 * Register the renderers this package owns: the value, computed and reference
 * kinds that need nothing from an app, and the reference chips it owns. An app
 * calls this once at startup, then registers the rest itself (see
 * registerFieldComponents, registerReferenceEntries, registerJunctionElements).
 *
 * Markdown, the pickers that read the app's stores, and the reference entries
 * that search an app's data are left to the app.
 */
export function registerDefaultFieldComponents(): void {
  registerFieldComponents({
    value: { cell: ValueField },

    // Value-kind overrides
    string: { form: StringInput },
    number: { form: NumberInput },
    currency: { cell: CurrencyField, form: CurrencyInput },
    boolean: { cell: BooleanField, form: BooleanInput },
    timestamp: { form: TimestampInput },
    select: { cell: SelectValueField, form: ValueSelect },
    visibility: { cell: VisibilityField, form: SegmentedSelect },
    deal_status: { cell: StatusSelectField, form: SegmentedSelect },
    task_status: { cell: StatusSelectField, form: StatusSelect },
    review_status: { cell: StatusSelectField, form: StatusSelect },
    object: { form: ObjectInput },

    // Reference kinds. Types with no `cell` entry inherit the reference root's
    // ReferenceField and pick their chip element from the reference registry.
    // Junctions (link/tagging/attachment/backlink) are `parent: "reference"`, so
    // they inherit cell (ReferenceField) and form (ReferenceInput).
    reference: { cell: ReferenceField, form: ReferenceInput },

    // Computed-kind overrides
    lookup: { cell: LookupField },
    record: { cell: RecordField }, // _record identity column — cell-only (no form)
    related: { cell: ReferenceField }, // record chips, one per related record — not record's single link
  })

  // The reference entries whose chips are in this package. An app adds the rest,
  // and the `search` functions, which read its data.
  registerReferenceEntries({
    // Contact chips for the surfaces that render a contact through this registry
    // (the form's read-only chips, group headers). The grid's contact cell is
    // ContactField, which types new values rather than searching for them.
    contact: { element: ContactElement },
    // The `socials` field's rows as a compact row of icon-only SocialElement
    // links (distinct from `contact`'s icon+text pills).
    socials: { element: SocialElement },
    // A time read where no time cell or input is mounted (a group header); the
    // grid cell and form input are TimeField / TimeInput in the app.
    time: { element: TimeElement },
    user: { element: UserElement, placeholder: "Add user..." },
    tagging: { element: TagElement, placeholder: "Add tag..." },
    // Records read their icon and colour from the value (withAppearance), so the
    // chip needs nothing from an app; the app adds the `search`.
    record: { element: RecordElement, placeholder: "Add record..." },
    backlink: { element: RecordElement },
  })
  registerJunctionElements({ tagging: { element: TagElement }, link: { element: RecordElement } })
}
